import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import {
  Snackbar,
  Alert,
  CircularProgress,
  Box,
  Button,
  Typography,
  Autocomplete,
  Avatar,
  useMediaQuery,
  useTheme,
  Paper, // Import Paper for Autocomplete custom dropdown
} from "@mui/material";
import { makeRequest } from "../../../assets/functions_helper";
import { CustomTextField } from "../../../components/textFields";
import { useNavigate } from "react-router-dom";

// Asegúrate de que la ruta a tu CustomTextField sea correcta
// Ejemplo: import { CustomTextField } from "./CustomTextField";
// O si está en una carpeta de componentes comunes:

const VerifyEmail = () => {
  const { verify_key } = useParams();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  const [loading, setLoading] = useState(true);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [snackbarSeverity, setSnackbarSeverity] = useState("success"); // 'success' | 'error' | 'info' | 'warning'

  const [accessToken, setAccessToken] = useState("");

  const [emailVerified, setEmailVerified] = useState(false);
  const [password, setPassword] = useState("");
  const [phone, setPhone] = useState("");
  const [phoneCode, setPhoneCode] = useState("");
  const [isPhoneCodeSent, setIsPhoneCodeSent] = useState(false);
  const [isPhoneVerified, setIsPhoneVerified] = useState(false);

  // Countdown para reenvío de código
  const [countdown, setCountdown] = useState(0);
  const [isCountdownActive, setIsCountdownActive] = useState(false);

  const [countries, setCountries] = useState([]);
  const [selectedCountry, setSelectedCountry] = useState(null);

  // Paleta de colores basada en tu CustomTextField y los de Admin
  const customThemeColors = {
    background: "#F9FAFB", // Fondo gris claro (Tailwind gray-50)
    textPrimary: "#111827", // Texto oscuro principal
    textSecondary: "#4B5563", // Texto secundario
    accent: "#E63946", // Color de acento principal rojo
    accentHover: "#D62828", // Rojo hover
    accentFocusedLabel: "#E63946",
    inputBackground: "#FFFFFF", 
    error: "#EF4444", 
    success: "#10B981", 
  };

  const navigate = useNavigate();

  useEffect(() => {
    makeRequest(
      "POST",
      "users/countries",
      {},
      "application/json",
      localStorage.getItem("token"),
      localStorage.getItem("tokenType"),
      () => {}
    )
      .then((response) => setCountries(response))
      .catch(() => {
        setSnackbarMessage("❌ Error al obtener la lista de países");
        setSnackbarSeverity("error");
        setSnackbarOpen(true);
      });
  }, []);

  useEffect(() => {
    if (verify_key) {
      setLoading(true); // Inicia la carga al intentar verificar
      makeRequest(
        "POST",
        `restaurant_admin/verify-email/${verify_key}`,
        null,
        "application/json",
        null,
        null,
        () => {}
      )
        .then((response) => {
          setAccessToken(response.access_token);
          setEmailVerified(true);
          setSnackbarMessage("✅ Tu email ha sido verificado exitosamente");
          setSnackbarSeverity("success");
        })
        .catch(() => {
          setSnackbarMessage("❌ Ocurrió un error al verificar tu email");
          setSnackbarSeverity("error");
          setEmailVerified(false);
        })
        .finally(() => {
          setSnackbarOpen(true);
          setLoading(false);
        });
    } else {
      setLoading(false); // No hay verify_key, no hay nada que cargar inicialmente
      setSnackbarMessage("❌ Clave de verificación no encontrada.");
      setSnackbarSeverity("error");
      setSnackbarOpen(true);
      setEmailVerified(false);
    }
  }, [verify_key]);

  // Temporizador de cuenta regresiva para reenvío
  useEffect(() => {
    let interval;
    if (isCountdownActive && countdown > 0) {
      interval = setInterval(() => {
        setCountdown((prev) => {
          if (prev <= 1) {
            clearInterval(interval);
            setIsCountdownActive(false);
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
    }
    return () => clearInterval(interval);
  }, [isCountdownActive, countdown]);

  const validatePassword = (passwordToValidate) => {
    const hasUpperCase = /[A-Z]/.test(passwordToValidate);
    const hasSpecialChar = /[^A-Za-z0-9]/.test(passwordToValidate);
    return hasUpperCase && hasSpecialChar;
  };

  const handleShowSnackbar = (message, severity) => {
    setSnackbarMessage(message);
    setSnackbarSeverity(severity);
    setSnackbarOpen(true);
  };

  const handleSendVerificationCode = () => {
    if (!selectedCountry) {
      handleShowSnackbar("❌ Por favor, selecciona un país", "error");
      return;
    }

    if (password.length < 8 || !validatePassword(password)) {
      handleShowSnackbar(
        "❌ La contraseña debe tener al menos 8 caracteres, una mayúscula y un carácter especial.",
        "error"
      );
      return;
    }

    makeRequest(
      "POST",
      "restaurant_admin/send_cellphone_verification",
      {
        password,
        cellphone_number: `${phone}`,
        country_id: selectedCountry.id,
      },
      "application/json",
      accessToken,
      "Bearer",
      () => {}
    )
      .then(() => {
        handleShowSnackbar("📲 Código enviado a tu teléfono", "success");
        setIsPhoneCodeSent(true);
        // Iniciar countdown de 30 segundos por defecto al enviar por primera vez
        setCountdown(30);
        setIsCountdownActive(true);
      })
      .catch((err) => {
        console.log(err);
        if (err["error_code"] === "0003") {
          handleShowSnackbar(
            "El número de telefono ya se encuentra registrado",
            "error"
          );
        } else if (err["error_code"] === "0009") {
          // Usar time_remaining del backend para el countdown exacto
          const waitTime = Math.ceil(err.time_remaining || err.detail?.time_remaining || 30);
          setCountdown(waitTime);
          setIsCountdownActive(true);
          handleShowSnackbar(
            `⏳ Espera ${waitTime} segundos antes de reenviar el código`,
            "warning"
          );
        } else {
          handleShowSnackbar("❌ No se pudo enviar el código", "error");
        }
      });
  };

  const handleVerifyPhoneCode = async () => {
    if (!phoneCode.trim()) {
      handleShowSnackbar(
        "❌ Por favor ingresa el código recibido por SMS",
        "error"
      );
      return;
    }

    setLoading(true); // Mostrar feedback de carga
    try {
      await makeRequest(
        "POST",
        `restaurant_admin/verify_cellphone_code/${phoneCode}`,
        null,
        "application/json",
        accessToken,
        "Bearer",
        () => {}
      );

      await makeRequest(
        "POST",
        "restaurant_admin/create_password",
        { password }, // Asegúrate de que esta sea la data correcta que espera tu backend
        "application/json",
        accessToken,
        "Bearer",
        () => {}
      );

      handleShowSnackbar(
        "✅ Tu número fue verificado y la contraseña se guardó exitosamente",
        "success"
      );
      setIsPhoneVerified(true);
    } catch (err) {
      handleShowSnackbar(
        "❌ Código inválido o error al crear la contraseña",
        "error"
      );
    } finally {
      setLoading(false);
    }
  };

  const commonButtonStyles = {
    backgroundColor: customThemeColors.accent,
    color: "#FFFFFF",
    borderRadius: "12px",
    padding: theme.spacing(1.5, 0),
    textTransform: "none",
    fontWeight: "bold",
    transition: "background-color 0.2s ease-in-out, border 0.2s ease-in-out",
    border: `1px solid ${customThemeColors.accent}`,
    boxShadow: "0 4px 6px -1px rgba(230, 57, 70, 0.2), 0 2px 4px -1px rgba(230, 57, 70, 0.1)",
    "&:hover": {
      backgroundColor: customThemeColors.accentHover,
      borderColor: customThemeColors.accentHover,
      boxShadow: "0 10px 15px -3px rgba(230, 57, 70, 0.3), 0 4px 6px -2px rgba(230, 57, 70, 0.1)",
    },
    "&:disabled": {
      backgroundColor: "#F3F4F6",
      color: "#9CA3AF",
      borderColor: "#E5E7EB",
      boxShadow: "none",
    },
  };

  if (loading) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        minHeight="100vh"
        sx={{ backgroundColor: customThemeColors.background }}
      >
        <CircularProgress
          sx={{ color: customThemeColors.accentFocusedLabel }}
        />
      </Box>
    );
  }

  return (
    <Box
      display="flex"
      justifyContent="center"
      alignItems="center"
      minHeight="100vh"
      sx={{ backgroundColor: customThemeColors.background }}
      padding={isMobile ? theme.spacing(2) : theme.spacing(4)}
    >
      <Paper
        elevation={0}
        sx={{
          display: "flex",
          flexDirection: "column",
          width: "100%",
          maxWidth: "480px",
          gap: isMobile ? 3 : 4,
          padding: isMobile ? theme.spacing(4) : theme.spacing(6),
          borderRadius: "24px",
          backgroundColor: "#FFFFFF",
          boxShadow: "0 10px 25px -5px rgba(0, 0, 0, 0.05), 0 8px 10px -6px rgba(0, 0, 0, 0.01)",
          border: "1px solid rgba(0,0,0,0.05)",
        }}
      >
      {!emailVerified ? (
        <Box textAlign="center">
          <Typography
            variant="h5"
            color={customThemeColors.error}
            align="center"
            sx={{ fontWeight: "bold", mb: 2 }}
          >
            😕 Error en la Verificación
          </Typography>
          <Button
            variant="contained"
            size="large"
            onClick={() => navigate("/login")}
            sx={commonButtonStyles}
          >
            Volver al Login
          </Button>
        </Box>
      ) : isPhoneVerified ? (
        <Box textAlign="center">
          <Typography
            variant="h5"
            sx={{ color: customThemeColors.success, fontWeight: "bold" }}
            gutterBottom
          >
            🎉 ¡Proceso Completado!
          </Typography>
          <Typography
            variant="body1"
            sx={{ color: customThemeColors.textSecondary, mb: 3 }}
          >
            Tu número ha sido verificado y la contraseña fue creada con éxito.
            Ya puedes iniciar sesión.
          </Typography>
          <Button
            variant="contained"
            fullWidth
            size="large"
            onClick={() => navigate("/login")}
            sx={commonButtonStyles}
          >
            Ir al Login
          </Button>
        </Box>
      ) : !isPhoneCodeSent ? (
        // Formulario para contraseña y teléfono
        <>
          <Typography
            variant="h5"
            align="center"
            sx={{ color: customThemeColors.textPrimary, fontWeight: "bold" }}
          >
            🔒 Completa tu Información
          </Typography>
          <Typography
            variant="body2"
            align="center"
            sx={{ color: customThemeColors.textSecondary, mb: 2 }}
          >
            Crea tu contraseña y verifica tu número de teléfono para continuar.
          </Typography>
          <CustomTextField
            label="Contraseña"
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            fullWidth
            helperText="Debe tener al menos 8 caracteres, una mayúscula y un carácter especial."
          />
          <CustomTextField
            label="Teléfono"
            type="tel"
            value={phone}
            onChange={(e) => setPhone(e.target.value)}
            fullWidth
          />
          <Autocomplete
            options={countries}
            getOptionLabel={(option) => `${option.name} (${option.code})`}
            renderOption={(props, option) => (
              <Box
                component="li"
                {...props}
                key={option.id}
                sx={{ display: "flex", alignItems: "center" }}
              >
                <Avatar
                  src={option.flag}
                  alt={option.name}
                  sx={{
                    mr: 1.5,
                    width: 24,
                    height: 24,
                    border: `1px solid ${customThemeColors.accent}`,
                  }}
                />
                <Typography
                  variant="body2"
                  sx={{ color: customThemeColors.textSecondary }}
                >
                  {option.name} ({option.code})
                </Typography>
              </Box>
            )}
            value={selectedCountry}
            onChange={(event, newValue) => setSelectedCountry(newValue)}
            fullWidth
            PaperComponent={(props) => (
              <Paper
                {...props}
                sx={{
                  backgroundColor: "#FFFFFF",
                  color: customThemeColors.textPrimary,
                  border: "1px solid #E5E7EB",
                  borderRadius: "12px",
                  marginTop: theme.spacing(1),
                  boxShadow: "0 10px 15px -3px rgba(0, 0, 0, 0.1)",
                  ".MuiAutocomplete-option": {
                    padding: theme.spacing(1, 1.5),
                    "&[aria-selected='true']": {
                      backgroundColor: "#FEE2E2",
                      color: customThemeColors.accent,
                      "&:hover": {
                        backgroundColor: "#FECACA",
                      },
                    },
                    "&:hover": {
                      backgroundColor: "#F3F4F6",
                    },
                  },
                  ".MuiAutocomplete-noOptions": {
                    color: customThemeColors.textSecondary,
                  },
                }}
              />
            )}
            renderInput={(params) => (
              <CustomTextField {...params} label="País" fullWidth />
            )}
          />
          <Button
            variant="contained"
            fullWidth
            size="large"
            onClick={handleSendVerificationCode}
            disabled={!password || !phone || !selectedCountry || loading}
            sx={commonButtonStyles}
          >
            {loading ? (
              <CircularProgress
                size={24}
                sx={{ color: "#FFFFFF" }}
              />
            ) : (
              "Enviar código"
            )}
          </Button>
        </>
      ) : (
        // Formulario para verificar código SMS
        <>
          <Typography
            variant="h5"
            align="center"
            sx={{ color: customThemeColors.textPrimary, fontWeight: "bold" }}
          >
            📱 Verifica tu Teléfono
          </Typography>
          <Typography
            variant="body2"
            align="center"
            sx={{ color: customThemeColors.textSecondary, mb: 2 }}
          >
            Ingresa el código de 5 dígitos enviado a tu número.
          </Typography>

          {/* ✅ CAMPO CORREGIDO: autocompletado deshabilitado */}
          <CustomTextField
            label="Código de verificación"
            value={phoneCode}
            onChange={(e) => {
              // Solo permitir dígitos
              const val = e.target.value.replace(/\D/g, "");
              setPhoneCode(val);
            }}
            fullWidth
            // Atributos a nivel de input (MUI los pasa al elemento <input> nativo)
            autoComplete="off"
            autoCorrect="off"
            autoCapitalize="off"
            spellCheck={false}
            // Nombre aleatorio para confundir al gestor de contraseñas del navegador
            name={`otp-field-${Math.random().toString(36).slice(2)}`}
            inputProps={{
              maxLength: 5,
              // "one-time-code" le indica al navegador que es un OTP
              // y suprime sugerencias de contraseñas/emails guardados
              autoComplete: "one-time-code",
              inputMode: "numeric",
              pattern: "[0-9]*",
              style: {
                textAlign: "center",
                letterSpacing: isMobile ? "0.5em" : "1em",
                fontSize: "1.2rem",
              },
            }}
            // InputProps afecta al componente MUI OutlinedInput que envuelve al <input>
            InputProps={{
              autoComplete: "off",
            }}
          />

          <Button
            variant="contained"
            fullWidth
            size="large"
            onClick={handleVerifyPhoneCode}
            disabled={!phoneCode || phoneCode.length < 5 || loading}
            sx={commonButtonStyles}
          >
            {loading ? (
              <CircularProgress
                size={24}
                sx={{ color: "#FFFFFF" }}
              />
            ) : (
              "Verificar Código"
            )}
          </Button>
          <Button
            onClick={() => {
              if (!isCountdownActive) handleSendVerificationCode();
            }}
            variant="contained"
            fullWidth
            size="large"
            disabled={isCountdownActive || loading}
            sx={commonButtonStyles}
          >
            {loading ? (
              <CircularProgress size={24} sx={{ color: "#FFFFFF" }} />
            ) : isCountdownActive ? (
              `Reenviar en ${countdown}s`
            ) : (
              "Reenviar Código"
            )}
          </Button>
        </>
      )}

      <Snackbar
        open={snackbarOpen}
        autoHideDuration={6000}
        onClose={() => setSnackbarOpen(false)}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert
          severity={snackbarSeverity}
          variant="filled" // Usar filled para mejor contraste con el tema oscuro
          onClose={() => setSnackbarOpen(false)}
          sx={{
            width: "100%",
            color: "#FFFFFF", // Texto blanco para la alerta
            backgroundColor:
              snackbarSeverity === "success"
                ? customThemeColors.success
                : customThemeColors.error,
            borderRadius: "8px",
            ".MuiAlert-icon": {
              color: "#FFFFFF", // Icono blanco
            },
          }}
        >
          {snackbarMessage}
        </Alert>
      </Snackbar>
      </Paper>
    </Box>
  );
};

export default VerifyEmail;